export const gestionStartupsAgil45 = {
  slug: "gestion-startups-agil-45",
  title: "Gestión de Startups: El Método de Innovación Ágil",
  durationMin: 45,
  programType: "ejecutivo",

  // Campos de presentación UI
  tagline: "Del caos a la tracción con metodología validada.",
  description: "Conversaciones en audio donde dos voces expertas desglosan los tres pilares del emprendimiento ágil: descubrir el mercado, gestionar la incertidumbre y ejecutar con herramientas concretas.",
  audienceDescription: "Fundadores · Product Managers · Directores de Innovación · Nivel inicial–intermedio",
  sourcesIntro: "Inspirado en los 3 libros fundacionales del movimiento Lean Startup",

  // Copy dinámico por programa — sobreescribe los defaults de ProgramLanding.jsx
  audienceDetail: "Fundadores en etapas tempranas, Product Managers que lideran iniciativas con incertidumbre alta, y profesionales de innovación que necesitan marcos de decisión basados en validación real, no en planes de negocio.",

  outcomeDetailed: "Serás capaz de formular hipótesis de negocio, diseñar experimentos mínimos para validarlas y tomar decisiones iterativas con datos reales — en lugar de construir meses antes de saber si alguien quiere lo que estás haciendo.",

  howItWorks: [
    {
      step: "01",
      title: "Escucha las 3 conversaciones",
      description: "Cada unidad son 15 min de audio con dos voces expertas. Sin relleno, sin motivación vacía.",
    },
    {
      step: "02",
      title: "Contrasta tu criterio",
      description: "Tras cada unidad hay un test de autodiagnóstico. No hay notas: es para que calibres dónde estás.",
    },
    {
      step: "03",
      title: "Completa el ejercicio final",
      description: "Un escenario real de decisión con tres preguntas de reflexión estructurada. Mínimo 100 caracteres por respuesta.",
    },
    {
      step: "04",
      title: "Reclama tu insignia",
      description: "Al terminar el ejercicio e iniciar sesión, obtienes una insignia digital verificable y compartible.",
    },
  ],

  outcome:
    "Validar hipótesis de negocio, reducir el desperdicio de recursos y tomar decisiones iterativas basadas en datos reales.",

  // --- SISTEMA DE INSIGNIA ---
  badgeEnabled: true,
  badgeLabel: "Insignia EPL — Validación ágil en startups",
  badgeSubtitle: "Micro-programa completado con ejercicio final de reflexión aplicada",
  badgeType: "completion",
  badgeVersion: 1,

  // --- EJERCICIO FINAL ---
  finalExerciseEnabled: true,
  finalExerciseType: "structured_reflection",
  finalExerciseVersion: 1,
  requiredConversationCount: 3,

  finalScenario: `Estás evaluando lanzar una startup con alta incertidumbre. Tienes tiempo y recursos limitados. Debes decidir tu primer movimiento.`,

  finalOptions: [
    { key: "A", label: "Construir el producto" },
    { key: "B", label: "Validar con clientes" },
    { key: "C", label: "Buscar inversión" },
  ],

  finalQuestions: [
    {
      id: "q1",
      type: "multiple_choice",
      required: true,
      text: "¿Cuál sería tu primer movimiento?",
    },
    {
      id: "q2",
      type: "text",
      required: true,
      minChars: 100,
      text: "¿Cuál es el mayor riesgo de la decisión que elegiste?",
    },
    {
      id: "q3",
      type: "text",
      required: true,
      minChars: 100,
      text: "¿Qué harías en las próximas 48 horas para reducir ese riesgo?",
    },
  ],

  feedbackByOption: {
    A: "Construir primero puede acelerar el aprendizaje técnico, pero suele fallar cuando aún no has validado necesidad real. La mayoría de los fracasos de startups se producen aquí: mucho esfuerzo construyendo algo que nadie quería.",
    B: "Validar con clientes reduce la incertidumbre más importante, pero puede generar falsos positivos si solo recoges opiniones y no señales reales de comportamiento. Busca early evangelists, no validación de cortesía.",
    C: "Buscar inversión temprano puede darte aire, pero también puede empujarte a escalar antes de haber probado el modelo. Los inversores en etapa pre-seed esperan ver tracción o al menos aprendizaje validado, no solo idea.",
  },
  sources: [
    {
      author: "Eric Ries",
      book: "The Lean Startup",
      institution: "IDEO / Y Combinator"
    },
    {
      author: "Steve Blank",
      book: "The Four Steps to the Epiphany",
      institution: "Stanford University"
    },
    {
      author: "Ash Maurya",
      book: "Running Lean",
      institution: "LEANSTACK"
    }
  ],

  units: [
    {
      slug: "u1-lean-startup-ries",
      order: 1,
      locked: false,
      title: "El ciclo Crear-Medir-Aprender",
      durationMin: 15,
      preAudioPhrase: "El éxito de una startup no es un arte místico ni cosa de genios. Es una ciencia. Un proceso que se puede aprender.",
      whyMatters: "Porque la mayoría de los fracasos emprendedores no se deben a falta de talento ni de capital, sino a meses o años de construcción sin validar si alguien quiere realmente lo que se está construyendo. El ciclo Crear-Medir-Aprender no es solo un proceso: es un cambio de mentalidad que convierte las suposiciones en datos antes de que sean demasiado caras de refutar.",
      objective:
        "Entender por qué la mayoría de startups fracasa no por falta de esfuerzo sino por construir algo que nadie quiere, y cómo el ciclo Crear-Medir-Aprender resuelve ese problema.",
      audioUrl: "/audio/gestion-startups-agil-45/u1-lean-startup-ries.m4a",
      source: {
        author: "Eric Ries",
        book: "The Lean Startup"
      },
      bullets: [
        "Las startups no fracasan por falta de dinero: fracasan por construir años algo que nadie quiere",
        "El plan de negocio de 50 páginas en un entorno de incertidumbre radical es un suicidio — la startup es un coche, no un cohete",
        "El único objetivo inicial de una startup es aprender: aprendizaje validado respaldado por datos de clientes reales, no intuición",
        "El PMV (Producto Mínimo Viable) no es un producto cutre — es el experimento más rápido para completar una vuelta del ciclo y reducir el riesgo de las hipótesis más arriesgadas",
        "Los indicadores vanidosos (usuarios totales, descargas) ocultan la verdad — el análisis de cohortes revela si el producto realmente mejora",
        "Pivotar no es fracasar: es una corrección de rumbo fundamental basada en datos, no en intuición",
      ],
      apply48h:
        "Identifica las dos asunciones de acto de fe de tu idea o proyecto: la hipótesis de valor (¿alguien lo quiere de verdad?) y la hipótesis de crecimiento (¿cómo te encontrarán nuevos clientes?). Luego diseña el PMV más pequeño posible para empezar a aprender esta semana, sin construir nada complejo.",
      test: {
        questions: [
          "Puedo identificar cuál es el supuesto más riesgoso de mi modelo de negocio actual.",
          "Cuando algo no funciona, analizo datos de comportamiento de usuarios antes de cambiar de dirección.",
          "Distingo claramente entre construir funcionalidades y validar hipótesis.",
        ],
        profiles: [
          {
            key: "constructor_sin_validar",
            range: [3, 7],
            label: "Constructor sin validar",
            advice: "Antes de construir más, define qué hipótesis estás probando. Recuerda: Zappos validó que la gente compraría zapatos online sin montar infraestructura — solo con fotos y una web simple.",
          },
          {
            key: "experimentador_emergente",
            range: [8, 11],
            label: "Experimentador emergente",
            advice: "Formaliza tu proceso: define métricas de éxito antes de lanzar cada experimento. Reemplaza métricas vanidosas por análisis de cohortes.",
          },
          {
            key: "practitioner_lean",
            range: [12, 15],
            label: "Practitioner Lean",
            advice: "Consolida cultura de aprendizaje en tu equipo: comparte aprendizajes validados semanalmente y documenta cada pivote con los datos que lo justificaron.",
          },
        ],
      },
    },
    {
      slug: "u2-customer-development-blank",
      order: 2,
      locked: false,
      title: "Salir del edificio: el mapa correcto para una startup",
      durationMin: 15,
      preAudioPhrase: "Webvan levantó 800 millones de dólares y quebró en 24 meses. No fracasó por mala suerte: usaba el mapa equivocado.",
      whyMatters: "Porque ejecutar bien un plan equivocado es el camino más eficiente hacia el fracaso. Blank demuestra con Webvan que la disciplina de ejecución sin mecanismos de aprendizaje es letal. La única forma de saber si el mapa es correcto es salir del edificio y contrastar cada suposición con personas reales antes de levantar la infraestructura que las da por ciertas.",
      objective:
        "Entender por qué el modelo tradicional de desarrollo de producto es tóxico para una startup y cómo el modelo de Desarrollo de Clientes permite validar el mercado antes de escalar y quemar capital.",
      audioUrl: "/audio/gestion-startups-agil-45/u2-customer-development-blank.m4a",
      source: {
        author: "Steve Blank",
        book: "The Four Steps to the Epiphany"
      },
      bullets: [
        "Una startup no es una versión en miniatura de una gran empresa: es una organización temporal que busca un modelo de negocio repetible y escalable — su trabajo es aprender, no ejecutar",
        "El modelo lineal construir-lanzar-vender es perfecto para Coca-Cola y tóxico para una startup: asume que ya conoces el cliente, el problema y el mercado",
        "No hay hechos dentro de tu edificio, solo opiniones — la única forma de convertir suposiciones en hechos es salir y contrastarlos con clientes reales",
        "El escalado prematuro (el error que mató a Webvan) ocurre cuando construyes infraestructura de multinacional antes de validar que el modelo funciona",
        "El early evangelist es más valioso que el early adopter: busca a quien tiene el problema, es consciente de él, busca solución activamente, ha improvisado algo propio y tiene presupuesto para resolverlo",
        "El tipo de mercado define toda la estrategia: mercado existente (compites), mercado nuevo (educas) o resegmentación (nicho o bajo coste) — usar la estrategia equivocada es suicidio",
      ],
      apply48h:
        "Agenda esta semana 3 conversaciones con potenciales clientes. No presentes tu solución. Haz solo estas preguntas: ¿cuál es el mayor problema que tienes en [área]? ¿Cómo lo resuelves hoy? ¿Cuánto te cuesta ese problema (tiempo, dinero, frustración)? Busca señales de early evangelist: quien ya ha improvisado algo para resolver ese dolor.",
      test: {
        questions: [
          "He tenido conversaciones reales con potenciales clientes en los últimos 30 días sin intentar venderles nada.",
          "Puedo describir el problema que resuelvo con las palabras exactas que usan mis clientes, no con las mías.",
          "Distingo entre feedback de cortesía ('qué buena idea') y señal real de demanda (alguien que saca la cartera o ya improvisó una solución).",
        ],
        profiles: [
          {
            key: "fundador_encerrado",
            range: [3, 7],
            label: "Fundador encerrado",
            advice: "Estás siguiendo el mapa de Webvan. Sal del edificio esta semana: 5 conversaciones reales con clientes potenciales valen más que 50 horas de desarrollo.",
          },
          {
            key: "descubridor_activo",
            range: [8, 11],
            label: "Descubridor activo",
            advice: "Estructura el aprendizaje: antes de cada entrevista define qué hipótesis específica quieres validar o refutar. Documenta patrones, no anécdotas.",
          },
          {
            key: "estratega_de_mercado",
            range: [12, 15],
            label: "Estratega de mercado",
            advice: "Ahora define con precisión en qué tipo de mercado juegas (existente, nuevo o resegmentado) y asegúrate de que tu estrategia de ventas y marketing corresponde a ese tipo.",
          },
        ],
      },
    },
    {
      slug: "u3-running-lean-maurya",
      order: 3,
      locked: false,
      title: "Lean Canvas: del Plan A al plan que funciona",
      durationMin: 15,
      preAudioPhrase: "Tu producto no es el producto. El verdadero producto es el modelo de negocio completo: que la gente lo encuentre, lo use, pague por ello y que todo eso te cueste menos de lo que ingresas.",
      whyMatters: "Porque enamorarse de la solución antes de validar el problema es la trampa más común y más cara del emprendimiento. Maurya aporta la herramienta concreta que les falta a Ries y Blank: el Lean Canvas como mapa vivo de hipótesis y el protocolo de entrevistas como brújula. Juntos permiten navegar desde el Plan A —que casi siempre está equivocado— hasta el plan que de verdad funciona, sin quedar a cero en el intento.",
      objective:
        "Usar el Lean Canvas como brújula para documentar hipótesis, salir a validarlas con entrevistas reales y encontrar el modelo de negocio que funciona antes de quedarte a cero.",
      audioUrl: "/audio/gestion-startups-agil-45/u3-running-lean-maurya.m4a",
      source: {
        author: "Ash Maurya",
        book: "Running Lean"
      },
      bullets: [
        "El error número uno es empezar el Lean Canvas por el bloque de solución — el orden correcto es: problema → segmento de clientes → propuesta de valor → solución (en ese orden)",
        "El precio no es algo que pones al final: es parte de tu producto, define a tu cliente y comunica tu valor — ponlo sobre la mesa en cada entrevista de solución",
        "Pivotar no es dar tumbos: es una corrección de rumbo estructurada basada en datos, distinta de optimizar (pequeños ajustes sobre algo que ya funciona)",
        "El MVP de Maurya para su propio libro no fue un capítulo: fue un taller presencial de pago que validó el problema, la solución y el precio antes de escribir una página",
        "Las encuestas y focus groups son inútiles al inicio — la única herramienta válida es la entrevista uno a uno, primero sin mencionar tu solución, luego con demo y precio real",
        "La ventaja injusta raramente es tu tecnología — es lo que se construye con el tiempo: comunidad, conocimiento profundo del cliente, datos exclusivos, confianza de marca",
      ],
      apply48h:
        "Completa el Lean Canvas en una hoja: empieza por el problema (3 problemas del segmento) y los early adopters (quién tiene ese dolor con más urgencia). Luego agenda 3 entrevistas orientadas al problema esta semana: no menciones tu solución, escucha cómo lo resuelven hoy. El Canvas es tu hipótesis; la entrevista es el experimento.",
      test: {
        questions: [
          "Tengo documentados el problema y el segmento de clientes antes de describir mi solución.",
          "He realizado entrevistas uno a uno donde no mencioné mi solución para escuchar el problema sin contaminar.",
          "Puedo describir qué es lo que hace que mi ventaja sea difícil de copiar o comprar, más allá de la tecnología.",
        ],
        profiles: [
          {
            key: "enamorado_de_la_solucion",
            range: [3, 7],
            label: "Enamorado de la solución",
            advice: "El audio de Maurya lo llama la trampa del fundador. Esta semana haz 3 entrevistas donde tengas prohibido hablar de tu solución. Solo escucha el problema.",
          },
          {
            key: "iterador_con_metodo",
            range: [8, 11],
            label: "Iterador con método",
            advice: "Pasa a las entrevistas de solución: presenta tu propuesta con un prototipo o boceto y di el precio directamente. La reacción ante el precio es la prueba de fuego.",
          },
          {
            key: "arquitecto_de_negocio",
            range: [12, 15],
            label: "Arquitecto de negocio",
            advice: "Ahora construye tu ventaja injusta activamente: documenta el conocimiento profundo de cliente que acumulas y convierte las conversaciones en comunidad o autoridad de marca.",
          },
        ],
      },
    },
  ],
};
